import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, User, Briefcase, UserPlus, FileText, Megaphone, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Type configurations with colors and icons
const TYPE_CONFIG = {
  agent: { 
    label: 'Agent', 
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    borderColor: 'border-purple-300',
    icon: User 
  },
  deal: { 
    label: 'Deal', 
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    borderColor: 'border-blue-300',
    icon: Briefcase 
  },
  lead: { 
    label: 'Lead', 
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    borderColor: 'border-green-300',
    icon: UserPlus 
  },
  report: { 
    label: 'Report', 
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    borderColor: 'border-amber-300',
    icon: FileText 
  },
  campaign: { 
    label: 'Campaign', 
    color: 'bg-coral-100 text-coral-800 dark:bg-orange-900 dark:text-orange-200',
    borderColor: 'border-orange-300',
    icon: Megaphone 
  }
};

// Type badge component
const TypeBadge = ({ type, small = false }) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.lead;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color} ${small ? 'text-[10px] px-1.5 py-0' : ''}`}>
      <Icon className={small ? "h-2.5 w-2.5" : "h-3 w-3"} />
      {config.label}
    </span>
  );
};

// Result row in dropdown
const LinkedToResultRow = ({ result, onSelect }) => {
  const config = TYPE_CONFIG[result.recordType] || TYPE_CONFIG.lead;
  const Icon = config.icon;
  
  return (
    <button
      type="button"
      onClick={() => onSelect(result)}
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
      data-testid={`link-result-${result.recordId}`}
    >
      <TypeBadge type={result.recordType} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
          {result.recordLabel}
        </div>
        {result.recordSubLabel && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {result.recordSubLabel}
          </div>
        )}
      </div>
    </button>
  );
};

// Selected chip component
const LinkedToChip = ({ record, onClear, readonly = false }) => {
  const config = TYPE_CONFIG[record.recordType] || TYPE_CONFIG.lead;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.borderColor} bg-gray-50 dark:bg-gray-800`}>
      <TypeBadge type={record.recordType} small />
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
        {record.recordLabel}
      </span>
      {!readonly && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          data-testid="clear-linked-record"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// Main LinkedToSelector component
const LinkedToSelector = ({ value, onChange, token }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(null); // null = All
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Debounced search
  const performSearch = useCallback(async (query, type) => {
    if (!query || query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({ q: query, token });
      if (type) {
        params.append('type', type);
      }
      
      const response = await axios.get(`${API_URL}/api/ceo/comments/link-search?${params}`);
      
      if (response.data.success) {
        setResults(response.data.data || []);
        setIsOpen(true);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);
  
  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce search (300ms)
    debounceRef.current = setTimeout(() => {
      performSearch(query, selectedType);
    }, 300);
  };
  
  // Handle type filter change
  const handleTypeChange = (type) => {
    setSelectedType(type);
    if (searchQuery.length >= 2) {
      performSearch(searchQuery, type);
    }
  };
  
  // Handle result selection
  const handleSelect = (result) => {
    onChange({
      recordType: result.recordType,
      recordId: result.recordId,
      recordLabel: result.recordLabel
    });
    setSearchQuery('');
    setResults([]);
    setIsOpen(false);
  };
  
  // Handle clear selection
  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  
  // Group results by type for "All" view
  const groupedResults = results.reduce((acc, result) => {
    const type = result.recordType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(result);
    return acc;
  }, {});
  
  // If a value is selected, show the chip
  if (value && value.recordLabel) {
    return (
      <div className="relative" data-testid="linked-to-selector">
        <LinkedToChip record={value} onClear={handleClear} />
      </div>
    );
  }
  
  return (
    <div className="relative" ref={dropdownRef} data-testid="linked-to-selector">
      {/* Type filter pills */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          type="button"
          onClick={() => handleTypeChange(null)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            selectedType === null 
              ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
          }`}
          data-testid="type-filter-all"
        >
          All
        </button>
        {Object.entries(TYPE_CONFIG).map(([type, config]) => (
          <button
            key={type}
            type="button"
            onClick={() => handleTypeChange(type)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              selectedType === type 
                ? config.color + ' ring-2 ring-offset-1 ring-gray-400' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
            }`}
            data-testid={`type-filter-${type}`}
          >
            {config.label}
          </button>
        ))}
      </div>
      
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Search agents, deals, leads..."
          className="pl-9 pr-8"
          data-testid="link-search-input"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>
      
      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
          {selectedType === null ? (
            // Grouped view for "All"
            Object.entries(groupedResults).map(([type, typeResults]) => (
              <div key={type}>
                <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-750 border-b border-gray-100 dark:border-gray-700">
                  <TypeBadge type={type} small />
                </div>
                {typeResults.map((result) => (
                  <LinkedToResultRow 
                    key={`${result.recordType}-${result.recordId}`}
                    result={result} 
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            ))
          ) : (
            // Flat view for specific type
            results.map((result) => (
              <LinkedToResultRow 
                key={`${result.recordType}-${result.recordId}`}
                result={result} 
                onSelect={handleSelect}
              />
            ))
          )}
        </div>
      )}
      
      {/* No results message */}
      {isOpen && searchQuery.length >= 2 && !isLoading && results.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 text-center text-sm text-gray-500">
          No results found for "{searchQuery}"
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      )}
    </div>
  );
};

// Display chip for HOD view (read-only, clickable)
export const LinkedToDisplayChip = ({ linkedTo, onClick }) => {
  if (!linkedTo || !linkedTo.recordLabel) {
    return null;
  }
  
  const config = TYPE_CONFIG[linkedTo.recordType] || TYPE_CONFIG.lead;
  const isDeleted = linkedTo.isDeleted;
  
  return (
    <button
      type="button"
      onClick={() => !isDeleted && onClick && onClick(linkedTo)}
      disabled={isDeleted}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
        isDeleted 
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800' 
          : `${config.color} hover:opacity-80 cursor-pointer`
      }`}
      data-testid={`linked-record-chip-${linkedTo.recordType}`}
    >
      <TypeBadge type={linkedTo.recordType} small />
      <span className={`truncate max-w-[150px] ${isDeleted ? 'line-through' : ''}`}>
        {linkedTo.recordLabel}
      </span>
      {isDeleted && <span className="text-[10px]">(deleted)</span>}
    </button>
  );
};

export { TypeBadge, LinkedToChip };
export default LinkedToSelector;
